import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';

const CommunityGuidelines = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Community Guidelines - Solis Games</title>
        <meta name="description" content="Community Guidelines for Solis Games - Learn about our community standards and how to create a positive gaming environment." />
      </Helmet>
      
      <Header />
      
      <main className="pt-16 pb-20 md:pb-8">
        <div className="container mx-auto px-4 lg:px-8 py-8 max-w-4xl">
          <div className="bg-card rounded-lg border border-border p-8">
            <h1 className="text-4xl font-bold text-foreground mb-6">Community Guidelines</h1>
            
            <p className="text-muted-foreground mb-8">
              Our community guidelines help create a safe, inclusive, and enjoyable gaming environment for everyone.
            </p>
            
            <div className="text-sm text-muted-foreground mb-8">
              <strong>This policy was last updated:</strong> February 26, 2025
            </div>

            <div className="prose prose-invert max-w-none">
              <p className="text-foreground mb-6">
                Welcome to the Solis Games community! These Community Guidelines outline the standards of behavior 
                we expect from all users of the Solis Games platform. By using our services, you agree to follow 
                these guidelines and help us maintain a positive gaming environment for everyone.
              </p>

              <h3 className="text-xl font-semibold text-foreground mb-3">1. Respect and Inclusivity</h3>
              <p className="text-foreground mb-4">
                We believe gaming should be fun and welcoming for everyone. To maintain this environment:
              </p>
              
              <ul className="list-disc pl-6 mb-6 text-foreground space-y-2">
                <li>Treat all players with respect, regardless of their skill level, background, or identity.</li>
                <li>Use inclusive language and avoid discriminatory comments based on race, gender, religion, sexual orientation, nationality, or disability.</li>
                <li>Be patient with new players and help create a welcoming environment for newcomers.</li>
                <li>Celebrate diversity and different perspectives within our gaming community.</li>
                <li>Remember that there's a real person behind every username.</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mb-3">2. Prohibited Behavior</h3>
              <p className="text-foreground mb-4">
                The following behaviors are strictly prohibited on the Solis Games platform:
              </p>
              
              <ul className="list-disc pl-6 mb-6 text-foreground space-y-2">
                <li><strong>Harassment and Bullying:</strong> Don't engage in persistent harassment, bullying, or targeted abuse of other players.</li>
                <li><strong>Hate Speech:</strong> No content that promotes hatred or violence against individuals or groups.</li>
                <li><strong>Threats and Violence:</strong> Don't make threats of violence or harm against other players or real-world individuals.</li>
                <li><strong>Doxxing:</strong> Never share personal information about other players without their consent.</li>
                <li><strong>Spam and Flooding:</strong> Don't spam chat channels or flood communications with repetitive messages.</li>
                <li><strong>Impersonation:</strong> Don't impersonate other players, staff members, or public figures.</li>
                <li><strong>Griefing:</strong> Don't intentionally ruin other players' gaming experience through disruptive behavior.</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mb-3">3. Fair Play and Cheating</h3>
              <p className="text-foreground mb-4">
                We're committed to maintaining fair and competitive gameplay:
              </p>
              
              <ul className="list-disc pl-6 mb-6 text-foreground space-y-2">
                <li><strong>No Cheating:</strong> Don't use hacks, exploits, bots, or any unauthorized third-party software.</li>
                <li><strong>Bug Reporting:</strong> If you discover a bug or exploit, report it to infosec@solisinteractive.com instead of using it for advantage.</li>
                <li><strong>Account Sharing:</strong> Don't share your account credentials or allow others to play on your account.</li>
                <li><strong>Boosting:</strong> Don't artificially inflate your stats or rankings through illegitimate means.</li>
                <li><strong>Match Manipulation:</strong> Don't intentionally lose games or manipulate match outcomes.</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mb-3">4. Communication Standards</h3>
              <p className="text-foreground mb-4">
                When communicating with other players through chat, voice, or other features:
              </p>
              
              <ul className="list-disc pl-6 mb-6 text-foreground space-y-2">
                <li>Keep conversations appropriate and family-friendly when possible.</li>
                <li>Avoid excessive profanity or offensive language.</li>
                <li>Don't share inappropriate content, including sexual, violent, or disturbing material.</li>
                <li>Respect others' privacy and don't pressure them to share personal information.</li>
                <li>Use constructive feedback rather than toxic criticism when discussing gameplay.</li>
                <li>Report inappropriate behavior rather than engaging in arguments.</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mb-3">5. Content and Sharing</h3>
              <p className="text-foreground mb-4">
                When creating or sharing content related to Solis Games:
              </p>
              
              <ul className="list-disc pl-6 mb-6 text-foreground space-y-2">
                <li>Don't share copyrighted material without proper authorization.</li>
                <li>Respect intellectual property rights of games and other players' creations.</li>
                <li>Don't create or share content that violates these community guidelines.</li>
                <li>When streaming or recording gameplay, follow platform-specific terms of service.</li>
                <li>Give credit where appropriate when sharing community-created content.</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mb-3">6. Privacy and Safety</h3>
              <p className="text-foreground mb-4">
                Protect yourself and others by following these safety guidelines:
              </p>
              
              <ul className="list-disc pl-6 mb-6 text-foreground space-y-2">
                <li>Don't share personal information like real names, addresses, phone numbers, or financial details.</li>
                <li>Be cautious about meeting online friends in person and always prioritize your safety.</li>
                <li>Don't click on suspicious links or download files from other players.</li>
                <li>Report any attempts at scamming, phishing, or social engineering.</li>
                <li>Use strong, unique passwords and enable two-factor authentication when available.</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mb-3">7. Reporting and Enforcement</h3>
              <p className="text-foreground mb-4">
                Help us maintain a positive community by reporting violations:
              </p>
              
              <ul className="list-disc pl-6 mb-6 text-foreground space-y-2">
                <li><strong>How to Report:</strong> Use in-game reporting tools or contact us at contact@solisinteractive.com.</li>
                <li><strong>What to Include:</strong> Provide specific details, screenshots, or other evidence when possible.</li>
                <li><strong>False Reports:</strong> Don't make false or malicious reports against other players.</li>
                <li><strong>Investigation Process:</strong> We review all reports and take appropriate action based on our findings.</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mb-3">8. Consequences for Violations</h3>
              <p className="text-foreground mb-4">
                Violations of these guidelines may result in:
              </p>
              
              <ul className="list-disc pl-6 mb-6 text-foreground space-y-2">
                <li><strong>Warnings:</strong> First-time or minor violations may result in a warning.</li>
                <li><strong>Temporary Restrictions:</strong> Chat restrictions, matchmaking penalties, or temporary suspensions.</li>
                <li><strong>Account Suspension:</strong> Temporary removal of access to some or all platform features.</li>
                <li><strong>Permanent Ban:</strong> Complete removal from the platform for severe or repeated violations.</li>
                <li><strong>Legal Action:</strong> In cases involving illegal activity, we may involve law enforcement.</li>
              </ul>

              <p className="text-foreground mb-6">
                The severity of consequences depends on factors such as the nature of the violation, previous infractions, 
                and impact on the community. We reserve the right to take immediate action for severe violations.
              </p>

              <h3 className="text-xl font-semibold text-foreground mb-3">9. Appeals Process</h3>
              <p className="text-foreground mb-6">
                If you believe you've been unfairly penalized, you can appeal by contacting us at contact@solisinteractive.com 
                with details about your case. We'll review appeals fairly and respond within a reasonable timeframe.
              </p>

              <h3 className="text-xl font-semibold text-foreground mb-3">10. Community Leadership</h3>
              <p className="text-foreground mb-6">
                We encourage positive community leadership. Players who consistently demonstrate good sportsmanship, 
                help others, and contribute positively to the community may be recognized through special programs 
                or community features.
              </p>

              <h3 className="text-xl font-semibold text-foreground mb-3">11. Updates to Guidelines</h3>
              <p className="text-foreground mb-6">
                These Community Guidelines may be updated periodically to address new issues or improve clarity. 
                We'll notify the community of significant changes through platform announcements or email notifications.
              </p>

              <h3 className="text-xl font-semibold text-foreground mb-3">12. Contact Us</h3>
              <p className="text-foreground mb-4">
                If you have questions about these guidelines or need to report an issue, contact us:
              </p>
              
              <ul className="list-disc pl-6 mb-6 text-foreground space-y-2">
                <li>General inquiries: <a href="mailto:contact@solisinteractive.com" className="text-primary hover:underline">contact@solisinteractive.com</a></li>
                <li>Security issues: <a href="mailto:infosec@solisinteractive.com" className="text-primary hover:underline">infosec@solisinteractive.com</a></li>
                <li>Address: 1440 W Taylor St PMB 4217 Chicago, Illinois 60607, United States</li>
              </ul>

              <div className="mt-8 p-4 bg-primary/10 border border-primary/20 rounded-lg">
                <p className="text-primary font-medium mb-2">Remember:</p>
                <p className="text-foreground">
                  These guidelines exist to ensure everyone can enjoy gaming in a safe and positive environment. 
                  By following them, you're helping to build a community that welcomes players of all backgrounds 
                  and skill levels. Thank you for being part of the Solis Games community!
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default CommunityGuidelines;
